import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  CircularProgress,
  Grid2,
  Paper,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { DailyTasksTatMetricId, DailyTasksTatStageCard } from "../../../../../../models/Dashboard/DailyTasks/DailyTasksTat";
import { DailyTasksStore } from "../../../../stores/dashboards/DailyTasksStore";
import {
  formatOpsTatAsOf,
  formatOpsTatCount,
  formatOpsTatDays,
  formatOpsTatDeltaLabel,
  formatOpsTatNumber,
  formatOpsTatPercent,
  formatOpsTatSignedDays,
  getOpsTatStatusColors,
} from "../ops-tat/opsTatFormat";
import { OpsTatStatus } from "../../../../../../models/Dashboard/OpsTat/OpsTatFilters";
import {
  dailyTasksStatusPillSx,
  getDailyTasksChipColors,
  dailyTasksStatusTone,
} from "./dailyTasksChips";
import {
  DailyTasksHint,
  DailyTasksSectionTitle,
  DAILY_TASKS_HINTS,
} from "./DailyTasksHint";
import { dailyTasksStageUi } from "./DailyTasksStageUi";

interface DailyTasksDaysLedgerProps {
  store: DailyTasksStore;
}

const clickableSx = {
  cursor: "pointer",
  outline: "none",
};

/** Grow by days, allow shrink so the spine fits the card with no horizontal scroll. */
const spineFlex = (days: number | null | undefined): string => {
  const grow =
    typeof days === "number" && Number.isFinite(days) && days > 0 ? days : 0.01;
  return `${grow} 1 0px`;
};

const OpsTatInlineState = ({
  message,
}: {
  message: string;
}) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="10rem"
      textAlign="center"
      border="1px dashed"
      borderColor="divider"
      borderRadius={2}
      px={2}
    >
      <CustomTypography
        variant="heading6"
        fontWeight={500}
        color={theme.palette.customColors.greyTextColor}
      >
        {message}
      </CustomTypography>
    </Box>
  );
};

const StatusDot = ({ status }: { status: OpsTatStatus | undefined }) => {
  const theme = useTheme();
  const colors = getOpsTatStatusColors(theme, status);
  return (
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: colors.fg,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
};

const DailyTasksDaysLedger: React.FC<DailyTasksDaysLedgerProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const customColors = theme.palette.customColors;
    const summary = store.summaryResult;
    const headline = summary?.data.headline;
    const stages = summary?.data.stages ?? [];
    const isFile = store.filters.stage === "file_upload";
    const isPr = store.filters.stage === "pr_creation";
    const isPo = store.filters.stage === "po_creation";
    const isRaw = store.filters.stage === "raw_inward";
    const isPrint = store.filters.stage === "print_outward";
    const isPrintIn = store.filters.stage === "print_inward";
    const stageUi = dailyTasksStageUi(store.filters.stage);
    const hasCycle =
      Boolean(headline?.cycle) && (headline?.orders ?? 0) > 0;

    const openMetric = async (
      metricId: DailyTasksTatMetricId,
      cardTitle?: string,
    ) => {
      try {
        await store.openDrawer(metricId, {
          cardTitle,
          cardSection: "days_ledger",
        });
      } catch (error) {
        // drilldownError is set on the store
      }
    };

    const onTileKeyDown = (
      event: React.KeyboardEvent,
      metricId: DailyTasksTatMetricId,
    ) => {
      try {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          void openMetric(metricId);
        }
      } catch (error) {
        // ignore keyboard handler failures
      }
    };

    const renderLegend = () => (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StatusDot status="ok" />
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            Within SLA
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StatusDot status="warn" />
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            At risk
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StatusDot status="bad" />
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            Breached
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 8,
              border: "1px solid",
              borderColor: customColors.lightGrayBorder,
              background: `repeating-linear-gradient(115deg, ${customColors.veryLightGray} 0 4px, ${customColors.white} 4px 7px)`,
            }}
          />
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
          >
            Committed SLA
          </CustomTypography>
        </Box>
      </Box>
    );

    const renderSpine = () => {
      const hatch = `repeating-linear-gradient(115deg, ${customColors.veryLightGray} 0 4px, ${customColors.white} 4px 7px)`;

      return (
        <Box sx={{ width: "100%", minWidth: 0, px: { xs: 0.5, sm: 0 } }}>
          <Box
            sx={{ display: "flex", height: { xs: 32, md: 38 }, gap: "2px" }}
          >
              {stages.map((stage) => (
                <SpineActualSegment
                  key={stage.stageId}
                  stage={stage}
                  onOpen={() => {
                    void openMetric(
                      stage.stageId as DailyTasksTatMetricId,
                      stage.name,
                    );
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", height: 12, gap: "2px", mt: "5px" }}>
              {stages.map((stage) => (
                <Box
                  key={`${stage.stageId}-plan`}
                  sx={{
                    flex: spineFlex(stage.target || 0),
                    minWidth: 0,
                    borderRadius: "2px",
                    border: "1px solid",
                    borderColor: customColors.lightGrayBorder,
                    background: hatch,
                  }}
                  title={`${stage.short} SLA ${formatOpsTatDays(stage.target)}`}
                />
              ))}
            </Box>
            {/* Equal-width columns so labels stay readable even when bars are skinny. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: `repeat(${Math.max(stages.length, 1)}, minmax(0, 1fr))`,
                },
                gap: "6px",
                mt: 1.25,
              }}
            >
              {stages.map((stage) => {
                const chip = getDailyTasksChipColors(
                  theme,
                  dailyTasksStatusTone(stage.status),
                );
                return (
                  <Box
                    key={`${stage.stageId}-tick`}
                    sx={{
                      minWidth: 0,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.35,
                      px: 0.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: customColors.white,
                      transition: "background-color .15s ease, border-color .15s ease",
                      "&:hover": {
                        borderColor: chip.fg,
                        bgcolor: `${chip.bg}`,
                      },
                    }}
                    role="button"
                    tabIndex={0}
                    title={`${stage.code} ${stage.short} · ${stage.name}`}
                    onClick={() => {
                      void openMetric(
                        stage.stageId as DailyTasksTatMetricId,
                        stage.name,
                      );
                    }}
                    onKeyDown={(event) =>
                      onTileKeyDown(
                        event,
                        stage.stageId as DailyTasksTatMetricId,
                      )
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      <StatusDot status={stage.status} />
                      <CustomTypography
                        variant="caption"
                        fontWeight={700}
                        color={customColors.navyText}
                        sx={{
                          lineHeight: 1.3,
                          fontSize: { xs: "0.65rem", md: "0.72rem" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {stage.code} · {stage.short}
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="caption"
                      color={customColors.greyTextColor}
                      sx={{
                        lineHeight: 1.3,
                        fontSize: { xs: "0.65rem", md: "0.72rem" },
                      }}
                    >
                      Used {formatOpsTatDays(stage.actual?.mean)}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      fontWeight={600}
                      color={chip.fg}
                      sx={{
                        lineHeight: 1.3,
                        fontSize: { xs: "0.65rem", md: "0.72rem" },
                      }}
                    >
                      {formatOpsTatSignedDays(stage.delta)} vs SLA
                    </CustomTypography>
                  </Box>
                );
              })}
            </Box>
          </Box>
      );
    };

    const renderLedgerBody = () => {
      if (store.summaryError && !summary) {
        return <OpsTatInlineState message={store.summaryError} />;
      }

      if (store.summaryLoading && !summary) {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="10rem"
          >
            <CircularProgress size={28} />
          </Box>
        );
      }

      if (!headline || !hasCycle) {
        const hasOrderFilter = (store.filters.order_id?.length ?? 0) > 0;
        const includeRunning = Boolean(store.filters.include_running);
        let message = "No completed cycle times for the current filters.";
        if (hasOrderFilter && !includeRunning) {
          message =
            "No finished cycles for the selected order(s). Clear Order ID, or set Include running to Yes to count in-flight work past SLA.";
        } else if (hasOrderFilter) {
          message =
            "No cycle samples for the selected order(s). Clear Order ID to see the full cohort, or pick an order that has finished this stage.";
        } else if (!includeRunning) {
          message =
            "No finished cycles in this date range. Widen the dates, or set Include running to Yes to include in-flight work already past SLA.";
        }
        return <OpsTatInlineState message={message} />;
      }

      const cycleStatus = headline.status;
      const topDrag = headline.top_drag;

      return (
        <>
          <Grid2 container spacing={0} columns={12}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Box
                role="button"
                tabIndex={0}
                sx={{
                  ...clickableSx,
                  p: 2,
                  height: "100%",
                  "&:hover": { bgcolor: theme.palette.action.hover },
                }}
                onClick={() => {
                  void openMetric("cycle_total", "Cycle total");
                }}
                onKeyDown={(event) => onTileKeyDown(event, "cycle_total")}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                    sx={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
                  >
                    {stageUi.cycleTitle}
                  </CustomTypography>
                  <DailyTasksHint
                    text={DAILY_TASKS_HINTS[stageUi.cycleHint]}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 1,
                    mt: 0.75,
                  }}
                >
                  <CustomTypography
                    variant="heading1"
                    color={customColors.navyText}
                    sx={{
                      fontSize: { xs: "2rem", md: "2.6rem" },
                      letterSpacing: "-0.03em",
                      lineHeight: 0.9,
                    }}
                  >
                    {formatOpsTatNumber(headline.cycle?.mean)}
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    color={customColors.greyTextColor}
                    sx={{ pb: 0.5 }}
                  >
                    days mean
                  </CustomTypography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 1.25,
                  }}
                >
                  <Box sx={dailyTasksStatusPillSx(theme, cycleStatus)}>
                    <CustomTypography
                      variant="caption"
                      fontWeight={600}
                      color={
                        getDailyTasksChipColors(
                          theme,
                          dailyTasksStatusTone(cycleStatus),
                        ).fg
                      }
                    >
                      {formatOpsTatDeltaLabel(headline.delta)}
                    </CustomTypography>
                  </Box>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                  >
                    SLA{" "}
                    <Box
                      component="span"
                      sx={{ color: customColors.navyText, fontWeight: 600 }}
                    >
                      {formatOpsTatDays(headline.target)}
                    </Box>
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                  >
                    P90{" "}
                    <Box
                      component="span"
                      sx={{ color: customColors.navyText, fontWeight: 600 }}
                    >
                      {formatOpsTatDays(headline.cycle?.p90)}
                    </Box>
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={customColors.greyTextColor}
                  >
                    <Box
                      component="span"
                      sx={{ color: customColors.navyText, fontWeight: 600 }}
                    >
                      {formatOpsTatCount(headline.orders)}
                    </Box>{" "}
                    orders
                    {headline.in_flight > 0 ? (
                      <>
                        {" · "}
                        <Box
                          component="span"
                          sx={{
                            color: customColors.navyText,
                            fontWeight: 600,
                          }}
                        >
                          {formatOpsTatCount(headline.in_flight)}
                        </Box>{" "}
                        in flight
                      </>
                    ) : null}
                  </CustomTypography>
                </Box>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Grid2 container spacing={0} columns={12} sx={{ height: "100%" }}>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <KpiTile
                    eyebrow="On SLA"
                    hint={DAILY_TASKS_HINTS[stageUi.slaShareHint]}
                    value={formatOpsTatPercent(headline.sla_adherence_pct)}
                    detail={
                      headline.breached == null
                        ? isFile
                          ? "No fully sent-to-printer orders in this window"
                          : isPr
                            ? "No orders with full PR qty in this window"
                            : isPo
                              ? "No orders with every PO emailed in this window"
                          : isRaw
                            ? "No orders with every issued-PO product QC-cleared in this window"
                          : isPrint
                            ? "No orders with every QC-cleared custom product fully outward in this window"
                          : isPrintIn
                            ? "No orders with every fully outward custom product print QC-cleared in this window"
                          : "No fully-approved orders in this window"
                        : `${formatOpsTatCount(headline.breached)} of ${formatOpsTatCount(headline.measured)} breached`
                    }
                    status={cycleStatus}
                    onClick={() => {
                      void openMetric("sla_adherence", "On-SLA orders");
                    }}
                    onKeyDown={(event) => onTileKeyDown(event, "sla_adherence")}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <KpiTile
                    eyebrow="Top drag"
                    hint={DAILY_TASKS_HINTS.topDrag}
                    value={topDrag?.name || "—"}
                    compactValue
                    detail={
                      topDrag
                        ? `${formatOpsTatSignedDays(topDrag.delta)} over, ${formatOpsTatPercent(topDrag.share_of_overrun_pct)} of overrun`
                        : "No stage overrun in this window"
                    }
                    status={null}
                    onClick={() => {
                      void openMetric(
                        "top_drag",
                        topDrag?.name
                          ? `Top drag · ${topDrag.name}`
                          : "Top drag",
                      );
                    }}
                    onKeyDown={(event) => onTileKeyDown(event, "top_drag")}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <KpiTile
                    eyebrow="Custom orders"
                    hint={DAILY_TASKS_HINTS.customMix}
                    value={formatOpsTatPercent(headline.custom_mix_pct)}
                    detail={`${formatOpsTatCount(headline.orders)} orders in cohort`}
                    status={null}
                    onClick={() => {
                      void openMetric("custom_mix", "Customised mix");
                    }}
                    onKeyDown={(event) => onTileKeyDown(event, "custom_mix")}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          </Grid2>

          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 0.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                mb: 1.5,
              }}
            >
              {renderLegend()}
            </Box>
            {stages.length > 0 ? (
              renderSpine()
            ) : (
              <OpsTatInlineState message="No stage means for the current path." />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
                borderTop: "1px dashed",
                borderColor: "divider",
                mt: 1.5,
                pt: 1,
                pb: 1.5,
              }}
            >
              <CustomTypography
                variant="caption"
                color={customColors.greyTextColor}
              >
                DAY 0 · {stageUi.cycleStartLabel}
              </CustomTypography>
              <CustomTypography
                variant="caption"
                color={customColors.greyTextColor}
              >
                Hatch = allowed SLA · solid = average days used
              </CustomTypography>
              <CustomTypography
                variant="caption"
                color={customColors.greyTextColor}
              >
                DAY {formatOpsTatNumber(headline.cycle?.mean)} ·{" "}
                {stageUi.cycleEndLabel}
                {summary?.meta.generated_at
                  ? ` · ${formatOpsTatAsOf(summary.meta.generated_at)}`
                  : ""}
              </CustomTypography>
            </Box>
          </Box>
        </>
      );
    };

    return (
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          position: "relative",
        }}
      >
        {store.summaryLoading && summary ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.45)",
              zIndex: 1,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}

        <DailyTasksSectionTitle
          title="Cycle time"
          hint={DAILY_TASKS_HINTS[stageUi.cycleHint]}
          subtitle={stageUi.cycleAverageBlurb}
        />

        {store.summaryError && summary ? (
          <Box sx={{ mb: 2 }}>
            <OpsTatInlineState message={store.summaryError} />
          </Box>
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          {renderLedgerBody()}
        </Paper>
      </Paper>
    );
  },
);

const SpineActualSegment = ({
  stage,
  onOpen,
}: {
  stage: DailyTasksTatStageCard;
  onOpen: () => void;
}) => {
  const theme = useTheme();
  const colors = getDailyTasksChipColors(
    theme,
    dailyTasksStatusTone(stage.status),
  );
  const actual = stage.actual?.mean;
  const labelColor =
    stage.status === "warn" || stage.status == null
      ? theme.palette.customColors.navyText
      : theme.palette.customColors.white;

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={`${stage.name} ${formatOpsTatDays(actual)}`}
      title={`${stage.code} · ${stage.short} · ${stage.name}`}
      sx={{
        flex: spineFlex(actual),
        minWidth: 0,
        border: 0,
        borderRadius: "2px",
        bgcolor: colors.fg,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        overflow: "hidden",
        "&:hover": { filter: "brightness(1.08)" },
      }}
    >
      <CustomTypography
        variant="caption"
        fontWeight={600}
        color={labelColor}
        sx={{
          fontSize: { xs: "0.65rem", md: "0.75rem" },
          px: 0.5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        {formatOpsTatNumber(actual)}
      </CustomTypography>
    </Box>
  );
};

const KpiTile = ({
  eyebrow,
  hint,
  value,
  detail,
  status,
  compactValue,
  onClick,
  onKeyDown,
}: {
  eyebrow: string;
  hint?: string;
  value: string;
  detail: string;
  status: OpsTatStatus | undefined;
  compactValue?: boolean;
  onClick: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      sx={{
        ...clickableSx,
        p: 2,
        height: "100%",
        borderLeft: { xs: "none", md: "1px solid" },
        borderTop: { xs: "1px solid", sm: "none", md: "none" },
        borderColor: "divider",
        "&:hover": { bgcolor: theme.palette.action.hover },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CustomTypography
          variant="caption"
          color={customColors.greyTextColor}
          sx={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
        >
          {eyebrow}
        </CustomTypography>
        {hint ? <DailyTasksHint text={hint} /> : null}
      </Box>
      <CustomTypography
        variant="heading4"
        color={customColors.navyText}
        sx={{
          mt: 0.75,
          mb: 0.5,
          fontSize: compactValue ? "1rem" : "1.4rem",
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </CustomTypography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <StatusDot status={status} />
        <CustomTypography variant="caption" color={customColors.greyTextColor}>
          {detail}
        </CustomTypography>
      </Box>
    </Box>
  );
};

export default DailyTasksDaysLedger;
